// File Name: pages/match-pages/matchI18n.js
//
// English + Arabic copy for the public match pages.
//
// Strings marked ✅ are lifted VERBATIM from the app's lib/l10n/ — the site and
// the app must not word the same idea two different ways. If you change one of
// those here, change it in the app too.

import { useCallback, useEffect, useState } from 'react';

export const STRINGS = {
  // ---- shared -------------------------------------------------------------
  brandSub: ['Yalla Nellab', 'يلا نلعب'],
  getTheApp: ['Get the app', 'احصل على التطبيق'],
  downloadYno: ['Download YNO', 'حمّل YNO'], // ✅ live.downloadYno
  openInApp: ['Open in the app', 'افتح في التطبيق'],
  notOnStores: [
    'The app is not published to the app stores yet — ask whoever invited you to send you the install file.',
    'لم يُنشر التطبيق على المتاجر بعد — اطلب ملف التثبيت ممّن دعاك.',
  ],

  // ---- join page ----------------------------------------------------------
  landingTitle: ['Football, organised.', 'كرة القدم، منظّمة.'],
  landingSub: [
    "Create matches, pick teams, keep score and track every player's stats.",
    'أنشئ المباريات، اختر الفرق، سجّل النتائج، وتابع إحصائيات كل لاعب.',
  ],
  haveCode: ['Have a match code?', 'لديك رمز مباراة؟'],
  matchCode: ['Match code', 'رمز المباراة'], // ✅ match.matchCode
  codeHint: ['e.g. AB12CD', 'مثال: AB12CD'], // ✅ match.matchCodeHint
  joinBtn: ['Join', 'انضم'],
  checking: ['Checking the code…', 'جارٍ التحقق من الرمز…'],
  enterMatchCode: ['Enter the match code.', 'أدخل رمز المباراة.'], // ✅
  noMatchForCode: ['No match found for that code.', 'لا توجد مباراة بهذا الرمز.'], // ✅
  couldNotLookup: ['Could not look up that code.', 'تعذّر البحث عن هذا الرمز.'], // ✅
  // ---- referral landing (/r/:code) ----------------------------------------
  // No longer dormant: the app's share message carries `nellab.org/r/<code>`
  // as of 2026-09-07, and ReferralPage.js renders this.
  refWelcome: ["You've been invited to YNO", 'لقد تمت دعوتك إلى YNO'],
  refGetApp: [
    'Install the app to claim your invite — you both earn points.',
    'ثبّت التطبيق للحصول على دعوتك — كلاكما يكسب نقاطًا.',
  ],
  refCodeLabel: ['Your referral code', 'رمز الإحالة الخاص بك'],
  // Reworded from "Enter it on the sign-up screen after you install the app."
  // — installing from the button below now fills the code in automatically, so
  // the old wording described work the visitor no longer has to do. It stays as
  // the fallback line, because the referrer does not survive every install
  // route (sideloads, phones with no Play Store).
  refEnterOnSignup: [
    "If it isn't filled in for you, enter it on the sign-up screen.",
    'إذا لم يُملأ تلقائيًا، أدخله في شاشة التسجيل.',
  ],
  refAutoApplied: [
    'Your code travels with the install — the app fills it in for you.',
    'ينتقل رمزك مع التثبيت — يملؤه التطبيق نيابةً عنك.',
  ],
  refCopy: ['Copy code', 'انسخ الرمز'],
  refCopied: ['✓ Copied', '✓ تم النسخ'],
  refOpenApp: ['Open YNO', 'افتح YNO'],
  // A /r link that arrived with no usable code — forwarded as plain text and
  // mangled somewhere along the way.
  refNoCode: [
    "This invite link is missing its code. Ask your friend to send it again — or just install the app and enter their code at sign-up.",
    'رابط الدعوة هذا ينقصه الرمز. اطلب من صديقك إرساله مجددًا — أو ثبّت التطبيق وأدخل رمزه عند التسجيل.',
  ],

  vs: ['vs', 'ضد'], // ✅ match.vs

  // ---- live page ----------------------------------------------------------
  liveTitle: ['Live', 'بث مباشر'], // ✅ live.liveTitle
  live: ['LIVE', 'مباشر'], // ✅ live.live
  paused: ['PAUSED', 'متوقفة'], // ✅ live.paused
  lobby: ['LOBBY', 'الردهة'], // ✅ live.lobby
  fullTime: ['Full Time', 'نهاية المباراة'], // ✅ live.fullTime
  extraTime: ['EXTRA TIME', 'الوقت الإضافي'], // ✅ live.extraTimeOpt
  halfTime: ['Half Time', 'الاستراحة'], // ✅ live.halfTime
  goals: ['GOALS', 'الأهداف'], // ✅ live.goals
  noGoals: ['No goals yet.', 'لا أهداف بعد.'], // ✅ live.noGoals
  goal: ['Goal', 'هدف'], // ✅ live.goal
  assist: ['assist', 'صناعة'], // ✅ live.assist
  player: ['PLAYER', 'لاعب'], // ✅ live.player
  players: ['PLAYERS', 'لاعبين'], // ✅ live.players
  noMatchSelected: ['No match selected', 'لم تُحدَّد مباراة'], // ✅
  openShareLink: [
    'Open a match’s share link to watch it live here.',
    'افتح رابط مشاركة مباراة لمتابعتها مباشرة هنا.',
  ], // ✅
  loadingMatch: ['Loading the match…', 'جارٍ تحميل المباراة…'],
  connectionLost: [
    'Lost connection to the match. Retrying…',
    'انقطع الاتصال بالمباراة. تجري إعادة المحاولة…',
  ],
  youWillJoin: ['You will join', 'ستنضم إلى'], // ✅ match.youWillJoin
  backToJoin: ['Enter a different code', 'أدخل رمزًا آخر'],

  // ---- guest join ---------------------------------------------------------
  // Copied from the app's guest_join_screen so the web and in-app flows read
  // identically.
  yourName: ['Your name', 'اسمك'], // ✅ match.yourName
  fullName: ['Full name', 'الاسم الكامل'], // ✅ match.fullName
  emailLabel: ['Email', 'البريد الإلكتروني'],
  emailHint: [
    'So your stats are saved for later',
    'كي تُحفظ إحصائياتك لاحقًا',
  ], // ✅ match.contactHintGuest
  // The line under the email field has now been removed TWICE. First
  // 'No account needed…' (untrue once the join started making accounts), then
  // its replacement `emailWhy`, removed 2026-08-21 at the user's request:
  //   EN 'We use it to find your YNO account — or create one for you, so your
  //       stats are saved.'
  //   AR 'نستخدمه للعثور على حسابك في YNO — أو لإنشاء حساب لك، كي تُحفظ إحصائياتك.'
  // Archived here and in SESSION_PROGRESS §63c — no git history in this project.
  // ⚠️ Nothing now tells the visitor BEFORE submitting that an account may be
  // created for their address; `accountCreatedNote` tells them after.
  enterYourEmail: ['Enter your email address.', 'أدخل بريدك الإلكتروني.'],
  joinTheMatch: ['Join the Match', 'انضم إلى المباراة'], // ✅ match.joinTheMatch
  joining: ['Joining…', 'جارٍ الانضمام…'], // ✅ match.joining
  enterYourName: ['Enter your name.', 'أدخل اسمك.'], // ✅ match.enterYourName
  enterValidEmail: ['Enter a valid email address.', 'أدخل بريدًا إلكترونيًا صحيحًا.'],
  couldNotJoin: ['Could not join. Try again.', 'تعذّر الانضمام. حاول مجددًا.'], // ✅
  matchOverJoin: ['That match is already over.', 'انتهت تلك المباراة بالفعل.'],
  matchStartedApprove: [
    'Match already started — the admin approves you before you play.',
    'بدأت المباراة بالفعل — يوافق المشرف عليك قبل أن تلعب.',
  ], // ✅ match.matchStartedApprove
  youWillJoinTeam: ['You will join', 'ستنضم إلى'], // ✅ match.youWillJoin
  joinedTitle: ["You're in", 'أنت مشارك'],
  joinedBody: [
    'You have been added to the line-up. Follow the match live below.',
    'تمت إضافتك إلى التشكيلة. تابع المباراة مباشرة أدناه.',
  ],
  // Shown once the join has resolved the email to an account.
  accountCreatedNote: [
    'We created a YNO account for you. Install the app and claim it to keep your stats.',
    'أنشأنا لك حسابًا في YNO. ثبّت التطبيق واستلم حسابك للاحتفاظ بإحصائياتك.',
  ],
  signedInNote: [
    'Joined with your existing YNO account.',
    'انضممت باستخدام حسابك الحالي في YNO.',
  ],
  alreadyInTitle: ["You're already in", 'أنت مشارك بالفعل'],
  alreadyInBody: [
    'You are already in this line-up. Follow the match live below.',
    'أنت مدرج في هذه التشكيلة بالفعل. تابع المباراة مباشرة أدناه.',
  ],
  pendingTitle: ['Waiting for approval', 'بانتظار الموافقة'],
  pendingBody: [
    'The match has started, so the host approves you before you play.',
    'بدأت المباراة، لذا يوافق عليك المضيف قبل أن تلعب.',
  ],
  watchLive: ['Follow the match live', 'تابع المباراة مباشرة'],
  // `justWatching` ('Just watching? Skip this' / 'تشاهد فقط؟ تخطَّ هذا') was
  // removed 2026-08-21 with the button it labelled. The text is archived in
  // SESSION_PROGRESS §63b — this project has no git history to recover it from.
};

const STORAGE_KEY = 'yno_lang';

function initialLang() {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved === '0' || saved === '1') return Number(saved);
  } catch (e) {
    /* private mode, storage blocked — fall through to the browser language */
  }
  const nav = (navigator.language || '').toLowerCase();
  return nav.startsWith('ar') ? 1 : 0;
}

/**
 * Language state for the match pages.
 *
 * Also drives `dir`/`lang` on <html>, because Arabic has to flip the whole
 * document. Both are restored on unmount so the rest of the marketing site is
 * never left in RTL.
 */
export function useMatchLang() {
  const [lang, setLang] = useState(initialLang);

  useEffect(() => {
    const html = document.documentElement;
    const prevDir = html.getAttribute('dir');
    const prevLang = html.getAttribute('lang');

    html.setAttribute('dir', lang === 1 ? 'rtl' : 'ltr');
    html.setAttribute('lang', lang === 1 ? 'ar' : 'en');

    return () => {
      if (prevDir === null) html.removeAttribute('dir');
      else html.setAttribute('dir', prevDir);
      if (prevLang === null) html.removeAttribute('lang');
      else html.setAttribute('lang', prevLang);
    };
  }, [lang]);

  const toggle = useCallback(() => {
    setLang((l) => {
      const next = l === 1 ? 0 : 1;
      try {
        window.localStorage.setItem(STORAGE_KEY, String(next));
      } catch (e) {
        /* nothing to do — the choice just will not persist */
      }
      return next;
    });
  }, []);

  const t = useCallback((key) => (STRINGS[key] || ['', ''])[lang] || '', [lang]);

  return { lang, isRtl: lang === 1, t, toggle };
}
